import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import * as d3 from 'd3';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CategoryPerformance = ({ data, onSelectCategory }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('most-mentioned');
  const [activeDetailTab, setActiveDetailTab] = useState('subcategories');
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: true, left: '50%', transform: 'translateX(-50%)' });
  const [activeEvidenceTab, setActiveEvidenceTab] = useState('positive');
  
  // Reference for subcategory items
  const subcategoryRefs = useRef({});
  
  // Extract category data from the processed_bank_data.json structure
  const processCategories = () => {
    if (!data) return [];
    
    const categories = {};
    const sentiments = {};
    
    // Process each review
    data.data.forEach(item => {
      if (item.processed_data && item.processed_data.categories) {
        item.processed_data.categories.forEach(cat => {
          // const categoryName = cat.category.replace('_', ' ');
          const categoryName = cat.category;
          
          // Initialize category if not exists
          if (!categories[categoryName]) {
            categories[categoryName] = 0;
            sentiments[categoryName] = { positive: 0, negative: 0, neutral: 0 };
          }
          
          // Increment count
          categories[categoryName]++;
          
          // Track sentiment
          if (cat.sentiment === 'positive') {
            sentiments[categoryName].positive++;
          } else if (cat.sentiment === 'negative') {
            sentiments[categoryName].negative++;
          } else {
            sentiments[categoryName].neutral++;
          }
        });
      }
    });
    
    // Format data for display
    return Object.keys(categories).map(name => {
      const total = categories[name];
      const positive = sentiments[name].positive;
      const negative = sentiments[name].negative;
      const neutral = sentiments[name].neutral;
      
      const positivePercentage = Math.round((positive / total) * 100);
      
      // Calculate trend (simplified in this demo)
      // In a real app, you'd compare with previous time periods
      let trend = 'stable';
      if (positivePercentage > 60) trend = 'up';
      else if (positivePercentage < 40) trend = 'down';
      
      return {
        name,
        mentioned: total,
        positivePercentage,
        trend
      };
    });
  };
  
  const categoryData = processCategories();
  
  // Sort by different metrics
  const mostMentioned = [...categoryData].sort((a, b) => b.mentioned - a.mentioned).slice(0, 6);
  const topPositive = [...categoryData].sort((a, b) => b.positivePercentage - a.positivePercentage).slice(0, 6);
  const topNegative = [...categoryData].sort((a, b) => a.positivePercentage - b.positivePercentage).slice(0, 6);
  
  // Helper function for trend icons
  const getTrendElement = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-3 h-3 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-3 h-3 text-red-500" />;
      case 'stable':
      default:
        return <Minus className="w-3 h-3 text-gray-500" />;
    }
  };
  
  // Handle category click
  const handleCategoryClick = (category) => {
    const newSelectedCategory = category === selectedCategory ? null : category;
    setSelectedCategory(newSelectedCategory);
    setActiveDetailTab('subcategories'); // Reset to first tab when changing categories
    
    // Call the parent callback if provided
    if (onSelectCategory) {
      onSelectCategory(newSelectedCategory);
    }
  };
  
  // Get subcategories for the selected category
  const getSubcategories = () => {
    if (!data || !selectedCategory) return [];
    
    const subcategories = {};
    // const catKey = selectedCategory.toLowerCase().replace(' ', '_');
    const catKey = selectedCategory.toLowerCase();
    
    data.data.forEach(item => {
      if (item.processed_data && item.processed_data.subcategories) {
        item.processed_data.subcategories.forEach(subcat => {
          if (subcat.category.toLowerCase() === catKey && subcat.subcategory !== 'Others') {
            const subcatName = subcat.subcategory;
            if (!subcategories[subcatName]) {
              subcategories[subcatName] = {
                name: subcatName, 
                count: 0,
                sentiment: { positive: 0, negative: 0, neutral: 0 },
                phrases: []
              };
            }
            
            subcategories[subcatName].count++;
            
            // Store relevant phrase from the review
            const phrase = item.text.length > 150 ? item.text.substring(0, 150) + '...' : item.text;
            subcategories[subcatName].phrases.push({
              text: phrase,
              sentiment: subcat.sentiment || 'neutral',
              id: item.id || `review-${Math.random().toString(36).substring(2, 9)}`
            });
            
            if (subcat.sentiment === 'positive') {
              subcategories[subcatName].sentiment.positive++;
            } else if (subcat.sentiment === 'negative') {
              subcategories[subcatName].sentiment.negative++;
            } else {
              subcategories[subcatName].sentiment.neutral++;
            }
          }
        });
      }
    });
    
    return Object.values(subcategories)
      .map(item => {
        const total = item.count;
        const positive = item.sentiment.positive;
        const negative = item.sentiment.negative;
        const neutral = item.sentiment.neutral;
        
        return {
          ...item,
          positivePercentage: Math.round((positive / total) * 100),
          negativePercentage: Math.round((negative / total) * 100),
          neutralPercentage: Math.round((neutral / total) * 100)
        };
      })
      .sort((a, b) => b.count - a.count);
  };
  
  // Format subcategories data for bar chart
  const formatSubcategoriesForChart = (subcategories) => {
    return subcategories.slice(0, 8).map(subcat => ({
      name: subcat.name.length > 12 ? subcat.name.substring(0, 12) + '...' : subcat.name,
      fullName: subcat.name,
      Positive: subcat.sentiment.positive,
      Negative: subcat.sentiment.negative,
      Neutral: subcat.sentiment.neutral,
      Total: subcat.count
    }));
  };

  // Custom tooltip for bar chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="text-sm font-medium">{payload[0]?.payload?.fullName || label}</p>
          <div className="mt-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center text-xs mt-1">
                <div 
                  className="w-2 h-2 rounded-full mr-2" 
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span>{entry.name}: {entry.value}</span>
              </div>
            ))}
          </div>
          <div className="text-xs font-medium mt-2 pt-1 border-t border-gray-200">
            Total: {payload.find(p => p.name === 'Total')?.value || 
                   payload.reduce((sum, entry) => 
                     entry.name !== 'Total' ? sum + entry.value : sum, 0)} mentions
          </div>
        </div>
      );
    }
    return null;
  };
  
  // Get topic clusters for the selected category (real data from subcategories)
  const getTopicClusters = () => {
    if (!data || !selectedCategory) return [];
    // Normalize category key
    const catKey = selectedCategory.toLowerCase();
    const topicMap = {};
    data.data.forEach(item => {
      if (item.processed_data && item.processed_data.subcategories) {
        item.processed_data.subcategories.forEach(subcat => {
          if ((subcat.category || '').toLowerCase() === catKey && subcat.subcategory !== 'Others') {
            const topic = subcat.subcategory;
            if (!topicMap[topic]) {
              topicMap[topic] = {
                name: topic.replace(/_/g, ' '),
                count: 0,
                sentiment: { positive: 0, negative: 0, neutral: 0 }
              };
            }
            topicMap[topic].count++;
            if (subcat.sentiment === 'positive') topicMap[topic].sentiment.positive++;
            else if (subcat.sentiment === 'negative') topicMap[topic].sentiment.negative++;
            else topicMap[topic].sentiment.neutral++;
          }
        });
      }
    });
    return Object.values(topicMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10 topics
  };
  
  // Mock competitive benchmark data
  const getCompetitiveBenchmark = () => {
    if (!selectedCategory) return { category: null, competitors: [] };
    
    const selected = categoryData.find(c => c.name === selectedCategory);
    
    return {
      category: selected,
      competitors: [
        { name: 'Competitor A', positivePercentage: Math.floor(Math.random() * 30) + 40, reviews: Math.floor(Math.random() * 100) + 50 },
        { name: 'Competitor B', positivePercentage: Math.floor(Math.random() * 30) + 40, reviews: Math.floor(Math.random() * 100) + 50 },
        { name: 'Industry Average', positivePercentage: 55, reviews: 500 }
      ]
    };
  };
  
  // Extract common words for word association
  const getWordAssociation = () => {
    if (!data || !selectedCategory) return [];
    
    const catKey = selectedCategory.toLowerCase().replace(' ', '_');
    const wordFreq = {};
    
    // Get relevant reviews for this category
    const relevantReviews = data.data.filter(item => 
      item.processed_data && 
      item.processed_data.categories && 
      item.processed_data.categories.some(cat => cat.category === catKey)
    );
    
    const stopWords = new Set(['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'this', 'that', 'these', 'those']);
    
    // Count word frequencies
    relevantReviews.forEach(item => {
      const words = item.text.toLowerCase().split(/\W+/).filter(word => 
        word.length > 3 && !stopWords.has(word)
      );
      
      words.forEach(word => {
        if (!wordFreq[word]) {
          wordFreq[word] = { word, count: 0 };
        }
        wordFreq[word].count++;
      });
    });
    
    // Get top words
    return Object.values(wordFreq)
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  };
  
  // Render the appropriate tab content
  const renderDetailContent = () => {
    if (!selectedCategory) return null;
    
    const selectedCategoryData = categoryData.find(c => c.name === selectedCategory);
    
    switch (activeDetailTab) {
      case 'subcategories':
        const subcategories = getSubcategories();
        
        // Function to calculate popover position
        const calculatePopoverPosition = (element, subcatName) => {
          if (!element) return;
          
          const rect = element.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Calculate available space in each direction
          const spaceAbove = rect.top;
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceLeft = rect.left;
          const spaceRight = viewportWidth - rect.right;
          
          // Determine position based on available space
          let position = {
            top: spaceBelow >= 250 || spaceBelow > spaceAbove, // Prefer below unless not enough space
            left: '50%',
            transform: 'translateX(-50%)'
          };
          
          // Adjust horizontal position if too close to edges
          if (rect.left < 300) {
            // Too close to left edge
            position.left = '0';
            position.transform = 'translateX(0)';
          } else if (viewportWidth - rect.right < 300) {
            // Too close to right edge
            position.left = 'auto';
            position.right = '0';
            position.transform = 'translateX(0)';
          }
          
          setPopoverPosition(position);
        };
        
        return (
          <div className="space-y-4 mt-4">
            <h4 className="text-sm font-medium text-gray-700">Subcategories of {selectedCategory}</h4>
            
            {/* Bar chart for subcategories */}
            {subcategories.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <div className="text-sm font-medium text-gray-700">
                    Top Subcategories by Mention Count
                  </div>
                  <div className="flex space-x-1">
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-1"></div>
                      <span>Positive</span>
                    </div>
                    <div className="flex items-center text-xs ml-2">
                      <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                      <span>Neutral</span>
                    </div>
                    <div className="flex items-center text-xs ml-2">
                      <div className="w-2 h-2 rounded-full bg-red-500 mr-1"></div>
                      <span>Negative</span>
                    </div>
                  </div>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={formatSubcategoriesForChart(subcategories)}
                      margin={{ top: 10, right: 10, left: 5, bottom: 20 }}
                      barGap={0}
                      barCategoryGap={8}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name"
                        tick={{ fontSize: 11, fill: '#4b5563' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#4b5563' }}
                        tickFormatter={(value) => value.toLocaleString()}
                      />
                      <Tooltip 
                        content={<CustomTooltip />}
                        cursor={{ fill: 'rgba(224, 231, 255, 0.2)' }}
                      />
                      <Bar 
                        dataKey="Positive" 
                        stackId="sentiment" 
                        fill="#22c55e" 
                        name="Positive" 
                        radius={[3, 3, 0, 0]} 
                      />
                      <Bar 
                        dataKey="Neutral" 
                        stackId="sentiment" 
                        fill="#94a3b8" 
                        name="Neutral" 
                        radius={[0, 0, 0, 0]} 
                      />
                      <Bar 
                        dataKey="Negative" 
                        stackId="sentiment" 
                        fill="#ef4444" 
                        name="Negative" 
                        radius={[0, 0, 3, 3]} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 text-xs text-gray-500 text-center">
                  Sentiment distribution for top {Math.min(subcategories.length, 8)} subcategories in {selectedCategory} category
                </div>
              </div>
            )}
            
            {subcategories.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {subcategories.map((subcat, index) => {
                  // Calculate gradient based on sentiment - with much lighter colors
                  const gradientStyle = {
                    background: `linear-gradient(135deg, 
                      rgba(34, 197, 94, ${subcat.positivePercentage / 100 * 0.2}), 
                      rgba(148, 163, 184, ${subcat.neutralPercentage / 100 * 0.15}), 
                      rgba(239, 68, 68, ${subcat.negativePercentage / 100 * 0.15}))`
                  };
                  
                  return (
                    <div 
                      key={index} 
                      ref={el => subcategoryRefs.current[subcat.name] = el}
                      className={`p-2 rounded-lg shadow-sm border border-gray-200 relative cursor-pointer transition-all duration-200 hover:shadow-md ${selectedSubcategory?.name === subcat.name ? 'ring-2 ring-blue-400' : ''}`}
                      style={gradientStyle}
                      onClick={(e) => {
                        const newSelected = selectedSubcategory?.name === subcat.name ? null : subcat;
                        setSelectedSubcategory(newSelected);
                        if (newSelected) {
                          calculatePopoverPosition(e.currentTarget, subcat.name);
                          
                          // Determine which tab to select based on available evidence
                          const positiveCount = newSelected.phrases.filter(p => p.sentiment === 'positive').length;
                          const negativeCount = newSelected.phrases.filter(p => p.sentiment === 'negative').length;
                          const neutralCount = newSelected.phrases.filter(p => p.sentiment === 'neutral').length;
                          
                          // Default to positive, but switch if needed
                          if (positiveCount > 0) {
                            setActiveEvidenceTab('positive');
                          } else if (negativeCount > 0) {
                            setActiveEvidenceTab('negative');
                          } else if (neutralCount > 0) {
                            setActiveEvidenceTab('neutral');
                          } else {
                            // If somehow all are zero, default to positive
                            setActiveEvidenceTab('positive');
                          }
                        }
                      }}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <div className="font-medium text-sm truncate text-slate-800" title={subcat.name}>{subcat.name}</div>
                        <div className="text-xs bg-white bg-opacity-80 text-gray-700 rounded-full px-2 py-0.5 whitespace-nowrap ml-1">{subcat.count}</div>
                      </div>
                      <div className="flex items-center">
                        <div className="flex-grow bg-white bg-opacity-70 h-2 rounded-full overflow-hidden">
                          <div className="flex h-full">
                            <div className="bg-green-500 h-full" style={{ width: `${subcat.positivePercentage}%` }}></div>
                            <div className="bg-gray-500 h-full" style={{ width: `${subcat.neutralPercentage}%` }}></div>
                            <div className="bg-red-500 h-full" style={{ width: `${subcat.negativePercentage}%` }}></div>
                          </div>
                        </div>
                        <div className="ml-2 text-xs font-medium bg-white bg-opacity-80 rounded-full px-1.5 text-gray-800">{subcat.positivePercentage}%</div>
                      </div>
                      
                      {/* Popover for phrases */}
                      {selectedSubcategory?.name === subcat.name && (
                        <div 
                          className={`absolute ${popoverPosition.top ? 'top-full' : 'bottom-full'} mt-1 z-20 bg-white rounded-lg shadow-xl border border-gray-200 p-3`} 
                          style={{ 
                            width: 'min(600px, 90vw)', 
                            left: popoverPosition.left,
                            right: popoverPosition.right,
                            transform: popoverPosition.transform
                          }}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <div className="font-medium">Evidence for "{subcat.name}"</div>
                            <button 
                              className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSubcategory(null);
                              }}
                            >
                              ✕
                            </button>
                          </div>
                          
                          {/* Sentiment filter tabs */}
                          <div className="flex border-b border-gray-200 mb-2">
                            <button
                              className={`py-1 px-2 text-xs font-medium rounded-t-md mr-1 ${
                                activeEvidenceTab === 'positive'
                                  ? 'bg-green-50 border-l border-r border-t border-green-200 text-green-600'
                                  : 'text-gray-500 hover:text-gray-700 bg-transparent'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveEvidenceTab('positive');
                              }}
                            >
                              Positive ({subcat.phrases.filter(p => p.sentiment === 'positive').length})
                            </button>
                            <button
                              className={`py-1 px-2 text-xs font-medium rounded-t-md mr-1 ${
                                activeEvidenceTab === 'negative'
                                  ? 'bg-red-50 border-l border-r border-t border-red-200 text-red-600'
                                  : 'text-gray-500 hover:text-gray-700 bg-transparent'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveEvidenceTab('negative');
                              }}
                            >
                              Negative ({subcat.phrases.filter(p => p.sentiment === 'negative').length})
                            </button>
                            <button
                              className={`py-1 px-2 text-xs font-medium rounded-t-md ${
                                activeEvidenceTab === 'neutral'
                                  ? 'bg-gray-50 border-l border-r border-t border-gray-200 text-gray-600'
                                  : 'text-gray-500 hover:text-gray-700 bg-transparent'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveEvidenceTab('neutral');
                              }}
                            >
                              Neutral ({subcat.phrases.filter(p => p.sentiment === 'neutral').length})
                            </button>
                          </div>
                          
                          <div className="overflow-y-auto max-h-60 pr-1" style={{ scrollbarWidth: 'thin' }}>
                            {subcat.phrases
                              .filter(phrase => phrase.sentiment === activeEvidenceTab)
                              .map((phrase, i) => (
                                <div key={i} className="py-2 px-2 border-b border-gray-100 text-sm last:border-0 hover:bg-gray-50">
                                  <div className="flex items-start gap-2">
                                    <span 
                                      className={`inline-block w-2 h-2 mt-1 rounded-full flex-shrink-0 ${
                                        phrase.sentiment === 'positive' ? 'bg-green-500' : 
                                        phrase.sentiment === 'negative' ? 'bg-red-500' : 'bg-gray-400'
                                      }`}
                                    ></span>
                                    <span className="text-gray-800">{phrase.text}</span>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-6 text-center">
                No subcategories found for {selectedCategory}
              </div>
            )}
          </div>
        );
        
      case 'benchmark':
        const benchmark = getCompetitiveBenchmark();
        return (
          <div className="space-y-4 mt-4">
            <h4 className="text-sm font-medium text-gray-700">Competitive Benchmark for {selectedCategory}</h4>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="mb-4">
                <div className="text-xs text-gray-500">How your {selectedCategory.toLowerCase()} performance compares:</div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-24 text-sm font-medium">Your Score</div>
                  <div className="flex-grow mx-3">
                    <div className="bg-gray-100 h-6 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${selectedCategoryData.positivePercentage >= 60 ? 'bg-green-500' : selectedCategoryData.positivePercentage >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${selectedCategoryData.positivePercentage}%` }}
                      >
                        <div className="h-full flex items-center justify-center text-xs text-white font-medium">
                          {selectedCategoryData.positivePercentage}%
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-medium w-24 text-right">{selectedCategoryData.mentioned} reviews</div>
                </div>
                
                {benchmark.competitors.map((competitor, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-24 text-sm font-medium">{competitor.name}</div>
                    <div className="flex-grow mx-3">
                      <div className="bg-gray-100 h-6 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${competitor.positivePercentage >= 60 ? 'bg-blue-400' : competitor.positivePercentage >= 40 ? 'bg-blue-300' : 'bg-blue-200'}`}
                          style={{ width: `${competitor.positivePercentage}%` }}
                        >
                          <div className="h-full flex items-center justify-center text-xs text-white font-medium">
                            {competitor.positivePercentage}%
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm font-medium w-24 text-right">{competitor.reviews} reviews</div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 text-xs text-gray-500">
                <p>* Competitive benchmark data is simulated for demonstration purposes.</p>
              </div>
            </div>
          </div>
        );
        
      case 'topics':
        return <TopicClusterVisualization category={selectedCategory} />;
        
      case 'wordassociation':
        return <WordAssociationNetwork category={selectedCategory} data={data} />;
        
      default:
        return null;
    }
  };
  
  // TopicClusterVisualization component using D3
  const TopicClusterVisualization = ({ category }) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    
    useEffect(() => {
      if (!svgRef.current || !containerRef.current) return;
      
      // Clear any existing visualization
      d3.select(svgRef.current).selectAll("*").remove();
      
      // Generate topic data
      const topics = getTopicClusters();
      if (!topics.length) return;
      
      // Set up dimensions
      const width = containerRef.current.clientWidth;
      const height = 400;
      const margin = { top: 20, right: 20, bottom: 20, left: 20 };
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;
      
      // Create SVG element
      const svg = d3.select(svgRef.current)
        .attr('width', width)
        .attr('height', height);
      
      const g = svg.append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);
      
      // Create a force simulation for bubble layout
      const simulation = d3.forceSimulation(topics)
        .force('x', d3.forceX(innerWidth / 2).strength(0.05))
        .force('y', d3.forceY(innerHeight / 2).strength(0.05))
        // Add more padding to reduce overlap
        .force('collide', d3.forceCollide().radius(d => Math.sqrt(d.count) * 4 + 18).strength(0.8));
      
      // Create a color scale for sentiment
      const getTopicColor = (topic) => {
        const positive = topic.sentiment.positive;
        const negative = topic.sentiment.negative;
        
        if (positive > 60) return '#22c55e'; // Mostly positive - green
        if (negative > 60) return '#ef4444'; // Mostly negative - red
        
        if (positive > negative) {
          return '#84cc16'; // More positive than negative - lime green
        } else if (negative > positive) {
          return '#f97316'; // More negative than positive - orange
        } else {
          return '#94a3b8'; // Balanced - gray
        }
      };
      
      // Create circles for each topic
      const bubbles = g.selectAll('.topic')
        .data(topics)
        .enter()
        .append('g')
        .attr('class', 'topic')
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          event.stopPropagation();
          setSelectedTopic(d === selectedTopic ? null : d);
        });
      
      // Add circle to each group
      bubbles.append('circle')
        .attr('r', d => Math.sqrt(d.count) * 4)
        .attr('fill', d => getTopicColor(d))
        .attr('fill-opacity', 0.8)
        .attr('stroke', d => d === selectedTopic ? '#1e40af' : '#fff')
        .attr('stroke-width', 1.5);
      
      // Add text for topic name (truncate, min font size, only show if big enough)
      bubbles.append('text')
        .text(d => d.name.length > 12 ? d.name.substring(0, 12) + '…' : d.name)
        .attr('text-anchor', 'middle')
        .attr('dy', '.3em')
        .attr('font-size', d => Math.max(Math.min(2.5 * Math.sqrt(d.count), 16), 8))
        .attr('fill', '#fff')
        .attr('font-weight', 'bold')
        .attr('pointer-events', 'none')
        .attr('stroke', '#000')
        .attr('stroke-width', 0.5)
        .attr('paint-order', 'stroke')
        .style('text-shadow', '0px 0px 3px rgba(0,0,0,0.5)')
        .attr('display', d => Math.sqrt(d.count) * 4 > 24 ? 'block' : 'none');
      
      // Add text for count (only show if big enough)
      bubbles.append('text')
        .text(d => d.count)
        .attr('text-anchor', 'middle')
        .attr('dy', '1.8em')
        .attr('font-size', '12px')
        .attr('fill', '#fff')
        .attr('font-weight', 'medium')
        .attr('pointer-events', 'none')
        .attr('stroke', '#000')
        .attr('stroke-width', 0.5)
        .attr('paint-order', 'stroke')
        .style('text-shadow', '0px 0px 2px rgba(0,0,0,0.5)')
        .attr('display', d => Math.sqrt(d.count) * 4 > 24 ? 'block' : 'none');
      
      // Update positions on each tick of the simulation
      simulation.on('tick', () => {
        bubbles.attr('transform', d => {
          // Keep bubbles within bounds
          d.x = Math.max(Math.sqrt(d.count) * 4, Math.min(innerWidth - Math.sqrt(d.count) * 4, d.x));
          d.y = Math.max(Math.sqrt(d.count) * 4, Math.min(innerHeight - Math.sqrt(d.count) * 4, d.y));
          
          return `translate(${d.x}, ${d.y})`;
        });
      });
      
      // Resize handler
      const handleResize = () => {
        if (containerRef.current) {
          const newWidth = containerRef.current.clientWidth;
          svg.attr('width', newWidth);
          
          // Update force simulation center
          simulation.force('x', d3.forceX(newWidth / 2).strength(0.05));
          simulation.restart();
        }
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [category, selectedTopic]);
    
    const topics = getTopicClusters();
    
    return (
      <div className="space-y-4 mt-4">
        <h4 className="text-sm font-medium text-gray-700">Topic Clusters for {category}</h4>
        {/* Visualization container */}
        <div 
          ref={containerRef} 
          className="border border-gray-200 rounded-lg bg-gray-50"
          style={{ height: '400px' }}
        >
          <svg ref={svgRef} width="100%" height="400"></svg>
          {topics.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">
              No topic clusters found for this category
            </div>
          )}
        </div>
        {/* Topic details when selected */}
        {selectedTopic && (
          <div className="bg-white p-4 border border-gray-200 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-medium">{selectedTopic.name}</h5>
              <span className="text-sm text-gray-500">{selectedTopic.count} mentions</span>
            </div>
            <div className="h-4 bg-gray-100 rounded-full overflow-hidden mb-2">
              <div className="flex h-full">
                <div 
                  className="bg-green-500 h-full" 
                  style={{ width: `${selectedTopic.sentiment.positive}%` }}
                ></div>
                <div 
                  className="bg-gray-400 h-full" 
                  style={{ width: `${selectedTopic.sentiment.neutral}%` }}
                ></div>
                <div 
                  className="bg-red-500 h-full" 
                  style={{ width: `${selectedTopic.sentiment.negative}%` }}
                ></div>
              </div>
            </div>
            <div className="flex justify-between text-xs">
              <div>
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1"></span>
                {selectedTopic.sentiment.positive}% Positive
              </div>
              <div>
                <span className="inline-block w-2 h-2 rounded-full bg-gray-400 mr-1"></span>
                {selectedTopic.sentiment.neutral}% Neutral
              </div>
              <div>
                <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1"></span>
                {selectedTopic.sentiment.negative}% Negative
              </div>
            </div>
          </div>
        )}
        <div className="text-xs text-gray-500">
          <p>* Circle size represents the number of mentions. Color represents the sentiment.</p>
          <p>* Click on a topic to see more details.</p>
        </div>
      </div>
    );
  };
  
  // WordAssociationNetwork component using D3
  const WordAssociationNetwork = ({ category, data }) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const [selectedWord, setSelectedWord] = useState(null);
    
    // Generate word links for the network
    const generateWordNetwork = () => {
      if (!data) return { nodes: [], links: [] };
      
      // const catKey = category.toLowerCase().replace(' ', '_');
      const catKey = category;
      
      // Get relevant reviews for this category
      const relevantReviews = data.data.filter(item => 
        item.processed_data && 
        item.processed_data.categories && 
        item.processed_data.categories.some(cat => cat.category === catKey)
      );
      
      const stopWords = new Set(['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'this', 'that', 'these', 'those', 'not']);
      
      // Word frequency and co-occurrence tracking
      const wordFreq = {};
      const wordPairs = {};
      const wordSentiment = {};
      
      // Process each review
      relevantReviews.forEach(item => {
        // Extract words
        const words = item.text.toLowerCase()
          .split(/\W+/)
          .filter(word => word.length > 3 && !stopWords.has(word))
          .slice(0, 20); // Limit to first 20 words for performance
        
        // Extract sentiment (using review sentiment or category sentiment if available)
        let sentiment = 'neutral';
        if (item.processed_data && item.processed_data.categories) {
          const catSentiment = item.processed_data.categories.find(cat => cat.category === catKey)?.sentiment;
          if (catSentiment) sentiment = catSentiment;
        }
        
        // Count word frequencies and track sentiment
        words.forEach(word => {
          if (!wordFreq[word]) {
            wordFreq[word] = { word, count: 0 };
            wordSentiment[word] = { positive: 0, negative: 0, neutral: 0 };
          }
          wordFreq[word].count++;
          
          // Track sentiment for each word
          if (sentiment === 'positive') {
            wordSentiment[word].positive++;
          } else if (sentiment === 'negative') {
            wordSentiment[word].negative++;
          } else {
            wordSentiment[word].neutral++;
          }
        });
        
        // Count co-occurrences (words appearing in same review)
        for (let i = 0; i < words.length; i++) {
          for (let j = i + 1; j < words.length; j++) {
            const wordA = words[i];
            const wordB = words[j];
            
            // Sort words alphabetically for consistent key
            const [first, second] = [wordA, wordB].sort();
            const pairKey = `${first}-${second}`;
            
            if (!wordPairs[pairKey]) {
              wordPairs[pairKey] = { source: first, target: second, value: 0 };
            }
            
            wordPairs[pairKey].value++;
          }
        }
      });
      
      // Get top words by frequency
      const topWords = Object.values(wordFreq)
        .sort((a, b) => b.count - a.count)
        .slice(0, 30);
      
      // Create a set of top words for filtering connections
      const topWordSet = new Set(topWords.map(w => w.word));
      
      // Filter connections to only include top words
      const connections = Object.values(wordPairs)
        .filter(pair => 
          topWordSet.has(pair.source) && 
          topWordSet.has(pair.target) &&
          pair.value > 1 // Only connections that occur more than once
        )
        .sort((a, b) => b.value - a.value)
        .slice(0, 40); // Limit number of connections
      
      // Format nodes for D3
      const nodes = topWords.map(item => {
        const sentiment = wordSentiment[item.word];
        const total = sentiment.positive + sentiment.negative + sentiment.neutral;
        const positivePercentage = total > 0 ? Math.round((sentiment.positive / total) * 100) : 0;
        const negativePercentage = total > 0 ? Math.round((sentiment.negative / total) * 100) : 0;
        
        return {
          id: item.word,
          count: item.count,
          sentiment: sentiment,
          positivePercentage: positivePercentage,
          negativePercentage: negativePercentage
        };
      });
      
      // Format links for D3
      const links = connections.map(conn => ({
        source: conn.source,
        target: conn.target,
        value: conn.value
      }));
      
      return { nodes, links };
    };
    
    useEffect(() => {
      if (!svgRef.current || !containerRef.current) return;
      
      // Clear any existing visualization
      d3.select(svgRef.current).selectAll("*").remove();
      
      // Generate network data
      const network = generateWordNetwork();
      
      // Exit if no data
      if (!network.nodes.length) return;
      
      // Set up dimensions
      const width = containerRef.current.clientWidth;
      const height = 400;
      
      // Create SVG element
      const svg = d3.select(svgRef.current)
        .attr('width', width)
        .attr('height', height);
      
      // Create a force simulation
      const simulation = d3.forceSimulation(network.nodes)
        .force('link', d3.forceLink(network.links).id(d => d.id).distance(100))
        .force('charge', d3.forceManyBody().strength(-200))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force('collision', d3.forceCollide().radius(d => Math.sqrt(d.count) * 2 + 10));
      
      // Create a scale for nodes based on count
      const nodeScale = d3.scaleSqrt()
        .domain([0, d3.max(network.nodes, d => d.count)])
        .range([4, 20]);
      
      // Create link scale for width
      const linkScale = d3.scaleLinear()
        .domain([0, d3.max(network.links, d => d.value)])
        .range([1, 5]);
      
      // Function to get color based on sentiment
      const getWordColor = (d) => {
        if (d.positivePercentage > 60) return '#22c55e'; // Mostly positive - green
        if (d.negativePercentage > 60) return '#ef4444'; // Mostly negative - red
        
        if (d.positivePercentage > d.negativePercentage) {
          return '#84cc16'; // More positive than negative - lime green
        } else if (d.negativePercentage > d.positivePercentage) {
          return '#f97316'; // More negative than positive - orange
        } else {
          return '#94a3b8'; // Balanced - gray
        }
      };
      
      // Draw links
      const link = svg.append('g')
        .selectAll('line')
        .data(network.links)
        .enter()
        .append('line')
        .attr('stroke-width', d => linkScale(d.value))
        .attr('stroke', '#999')
        .attr('stroke-opacity', 0.6);
      
      // Draw nodes
      const node = svg.append('g')
        .selectAll('g')
        .data(network.nodes)
        .enter()
        .append('g')
        .call(d3.drag()
          .on('start', dragstarted)
          .on('drag', dragged)
          .on('end', dragended))
        .on('click', (event, d) => {
          event.stopPropagation();
          setSelectedWord(d === selectedWord ? null : d);
        });
      
      // Add circles to nodes
      node.append('circle')
        .attr('r', d => nodeScale(d.count))
        .attr('fill', d => getWordColor(d))
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);
      
      // Add text labels
      node.append('text')
        .text(d => d.id)
        .attr('x', 0)
        .attr('y', d => -nodeScale(d.count) - 3)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#4b5563');
      
      // Update positions on each tick
      simulation.on('tick', () => {
        link
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y);
        
        node
          .attr('transform', d => {
            // Keep nodes within bounds
            d.x = Math.max(nodeScale(d.count), Math.min(width - nodeScale(d.count), d.x));
            d.y = Math.max(nodeScale(d.count), Math.min(height - nodeScale(d.count), d.y));
            
            return `translate(${d.x},${d.y})`;
          });
      });
      
      // Drag functions
      function dragstarted(event, d) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      }
      
      function dragged(event, d) {
        d.fx = event.x;
        d.fy = event.y;
      }
      
      function dragended(event, d) {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      }
      
      // Resize handler
      const handleResize = () => {
        if (containerRef.current) {
          const newWidth = containerRef.current.clientWidth;
          svg.attr('width', newWidth);
          
          // Update force simulation center
          simulation.force('center', d3.forceCenter(newWidth / 2, height / 2));
          simulation.restart();
        }
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [category, data, selectedWord]);
    
    const network = generateWordNetwork();
    
    return (
      <div className="space-y-4 mt-4">
        <h4 className="text-sm font-medium text-gray-700">Word Association Network for {category}</h4>
        
        {network.nodes.length > 0 ? (
          <>
            {/* Visualization container */}
            <div 
              ref={containerRef} 
              className="border border-gray-200 rounded-lg bg-gray-50"
              style={{ height: '400px' }}
            >
              <svg ref={svgRef} width="100%" height="400"></svg>
            </div>
            
            {/* Selected word details */}
            {selectedWord && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <h5 className="font-medium text-sm mb-1">"{selectedWord.id}"</h5>
                <div className="text-xs text-gray-600">Mentioned {selectedWord.count} times in feedback</div>
                <div className="mt-2 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className="flex h-full">
                    <div 
                      className="bg-green-500 h-full" 
                      style={{ width: `${selectedWord.positivePercentage}%` }}
                    ></div>
                    <div 
                      className="bg-gray-400 h-full" 
                      style={{ width: `${100 - selectedWord.positivePercentage - selectedWord.negativePercentage}%` }}
                    ></div>
                    <div 
                      className="bg-red-500 h-full" 
                      style={{ width: `${selectedWord.negativePercentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span>{selectedWord.positivePercentage}% positive</span>
                  <span>{selectedWord.negativePercentage}% negative</span>
                </div>
              </div>
            )}
            
            {/* Legend */}
            <div className="flex flex-wrap justify-between text-xs text-gray-500">
              <div className="flex items-center mr-3">
                <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1"></span>
                <span>Positive</span>
              </div>
              <div className="flex items-center mr-3">
                <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1"></span>
                <span>Negative</span>
              </div>
              <div className="flex items-center mr-3">
                <span className="inline-block w-3 h-3 rounded-full bg-gray-400 mr-1"></span>
                <span>Neutral</span>
              </div>
              <div>
                <span className="inline-block w-6 h-1 bg-gray-400 mr-1"></span>
                <span>Connection</span>
              </div>
              <div className="text-xs text-gray-500">
                <p>Tip: Drag words to rearrange. Click to select.</p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-sm text-gray-500 py-6 text-center">
            No word association data available for {category}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Category Performance</h3>
          <p className="text-sm text-gray-500">Analysis of feedback sentiments by category</p>
        </div>
        {selectedCategory && (
          <button 
            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 py-1 px-3 rounded-md"
            onClick={() => handleCategoryClick(null)}
          >
            Back to Overview
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left column - Categories list */}
        <div className={selectedCategory ? "md:col-span-1" : "md:col-span-3"}>
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex">
              <button
                className={`mr-8 py-2 px-1 text-sm font-medium ${
                  activeTab === 'most-mentioned'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('most-mentioned')}
              >
                Most Mentioned
              </button>
              <button
                className={`mr-8 py-2 px-1 text-sm font-medium ${
                  activeTab === 'top-positive'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('top-positive')}
              >
                Top Positive
              </button>
              <button
                className={`py-2 px-1 text-sm font-medium ${
                  activeTab === 'top-negative'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('top-negative')}
              >
                Top Negative
              </button>
            </nav>
          </div>
          
          {/* Category Grid */}
          <div className={`grid ${selectedCategory ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"} gap-4`}>
            {(activeTab === 'most-mentioned' ? mostMentioned : 
              activeTab === 'top-positive' ? topPositive : 
              topNegative).map((category, index) => {
              // Calculate gradient based on positivePercentage - with lighter colors
              const gradientStyle = {
                background: category.positivePercentage >= 60 
                  ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.05), rgba(34, 197, 94, 0.1))' 
                  : category.positivePercentage >= 40 
                    ? 'linear-gradient(135deg, rgba(234, 179, 8, 0.05), rgba(234, 179, 8, 0.1))' 
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05), rgba(239, 68, 68, 0.1))'
              };
              
              return (
                <div 
                  key={index} 
                  className={`${selectedCategory === category.name ? "border-blue-300 ring-2 ring-blue-200": " border-gray-100"} flex items-center border rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer group`}
                  onClick={() => handleCategoryClick(category.name)}
                  style={gradientStyle}
                >
                  <div className="flex-grow">
                    <div className="flex items-center">
                      <h3 className="font-medium text-sm group-hover:text-blue-600">{category.name}</h3>
                      <span className="ml-1 text-xs">{getTrendElement(category.trend)}</span>
                    </div>
                    <div className="text-xs text-gray-500">Mentioned in {category.mentioned} reviews</div>
                    <div className="mt-2 flex items-center">
                      <div className="flex-grow bg-gray-100 h-2 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${category.positivePercentage >= 60 ? 'bg-green-500' : category.positivePercentage >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${category.positivePercentage}%` }}
                        ></div>
                      </div>
                      <div className="ml-2 text-xs font-medium">{category.positivePercentage}%</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Right column - Category details */}
        {selectedCategory && (
          <div className="md:col-span-2">
            <div className="bg-gray-50 rounded-xl p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{selectedCategory}</h3>
                <div className="text-sm text-gray-500">
                  {categoryData.find(c => c.name === selectedCategory)?.mentioned} mentions
                </div>
              </div>
              
              {/* Detail Tab Navigation */}
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  className={`mr-6 py-2 px-3 text-sm font-medium rounded-t-md ${
                    activeDetailTab === 'subcategories'
                      ? 'bg-white border-l border-r border-t border-gray-200 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700 bg-transparent'
                  }`}
                  onClick={() => setActiveDetailTab('subcategories')}
                >
                  Subcategories
                </button>
                {/* <button
                  className={`mr-6 py-2 px-3 text-sm font-medium rounded-t-md ${
                    activeDetailTab === 'benchmark'
                      ? 'bg-white border-l border-r border-t border-gray-200 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700 bg-transparent'
                  }`}
                  onClick={() => setActiveDetailTab('benchmark')}
                >
                  Competitive Benchmark
                </button> */}
                <button
                  className={`mr-6 py-2 px-3 text-sm font-medium rounded-t-md ${
                    activeDetailTab === 'topics'
                      ? 'bg-white border-l border-r border-t border-gray-200 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700 bg-transparent'
                  }`}
                  onClick={() => setActiveDetailTab('topics')}
                >
                  Topic Clusters
                </button>
                <button
                  className={`py-2 px-3 text-sm font-medium rounded-t-md ${
                    activeDetailTab === 'wordassociation'
                      ? 'bg-white border-l border-r border-t border-gray-200 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700 bg-transparent'
                  }`}
                  onClick={() => setActiveDetailTab('wordassociation')}
                >
                  Word Association
                </button>
              </div>
              
              {/* Detail Content */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                {renderDetailContent()}
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default CategoryPerformance; 